import styled from 'styled-components';
import { Experience } from '@/webapi/use-experience-api';
import {
  PriceTestProductSummary,
  PriceTestProductVariation,
} from '@/features/details/price-test-reports/api';
import { DefaultTypography } from '@/components/typography';
import { breakpoints } from '@/components/responsive';
import { MetricKind } from '@/webapi/models';
import { capitalize } from '@/utils/types';
import { maybe } from '@/features/details/utils';

export const DeskHidden = styled.div`
  @media (min-width: 768px) {
    display: none;
  }
`;
export const MobileHidden = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;
export const format = (n: number, prefix = ``, suffix = ``) => {
  if (!Number.isFinite(n * 1)) {
    return `--`;
  }

  return `${prefix}${fmt.format(n)}${suffix}`;
};
const fmt = new Intl.NumberFormat(`en-US`, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
export const getVariantName = (
  experience: Experience,
  v: PriceTestProductVariation,
) => experience?.variants?.find((x) => x.id === v.useCaseVariant)?.name || ``;

export const SummaryBody = styled(DefaultTypography)`
  && {
    ${(p) =>
      p.col
        ? `display: flex;flex-direction:column; align-items: start; justify-content: space-between;`
        : ``}
    span {
      position: relative;
      font-size: ${(p) => p.fs || `1.4rem`};
      color: #111111;
      font-weight: 700;
      text-decoration: none;
      user-select: none;

      ${breakpoints.down(`md`)} {
        font-size: 1.2rem;
        font-family: Inter, sans-serif;
        color: black;
      }
    }
  }
`;
export const SummaryHeader = styled(DefaultTypography)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    color: #8fa3ad;
    font-size: 1.1rem;
    font-weight: 500;
  }
`;
export const defaultInitialPriceTestGoals = () => [
  MetricKind.PRODUCT_CR,
  MetricKind.PRODUCT_REVENUE,
  MetricKind.PRODUCT_DETAILS_VIEWS_RATE,
];

export function formatPriceTestGoal(value: MetricKind): string {
  let out = ``;

  if (value) {
    switch (value) {
      case MetricKind.PRODUCT_ATC_RATE:
        out = `Add to Cart %`;
        break;
      case MetricKind.PRODUCT_RETURNS:
        out = `Returns`;
        break;
      case MetricKind.PRODUCT_RETURN_PERCENT:
        out = `Returns %`;
        break;
      case MetricKind.PRODUCT_CR:
        out = `Conversion Rate`;
        break;
      case MetricKind.PRODUCT_PSV:
        out = `Per Session Value`;
        break;
      case MetricKind.PRODUCT_ITEMS_SOLD:
        out = `Items Sold`;
        break;
      case MetricKind.PRODUCT_REVENUE:
        out = `Revenue`;
        break;
      case MetricKind.PRODUCT_PROFIT:
        out = `Profit`;
        break;
      case MetricKind.PRODUCT_PROFIT_PERCENT:
        out = `Profit %`;
        break;
      case MetricKind.PRODUCT_PROFIT_PER_SESSION:
        out = `Profit per Session`;
        break;
      case MetricKind.PRODUCT_TRANSACTIONS:
        out = `Transactions`;
        break;
      case MetricKind.PRODUCT_IPP:
        out = `Items per Purchase`;
        break;
      case MetricKind.PRODUCT_DETAILS_VIEWS_RATE:
        out = `Product Detail Views %`;
        break;
      default:
        out = `${value}`.replaceAll(`_`, ` `);
    }
    out = capitalize(out.toLowerCase());
  }
  return out;
}

export const isPriceTestGoal = (m) =>
  m?.toString()?.startsWith(`PRODUCT`) &&
  m !== MetricKind.PRODUCT_DETAILS_VIEWS_RATE;
export const experiencePriceTestGoals = (experience: Experience) => {
  let priceTestGoals = experience.goals.filter(isPriceTestGoal);
  if (priceTestGoals?.length === 0) {
    priceTestGoals = defaultInitialPriceTestGoals();
  }
  return priceTestGoals;
};
export const allPriceTestGoals = () => [
  MetricKind.PRODUCT_ATC_RATE,
  MetricKind.PRODUCT_RETURNS,
  MetricKind.PRODUCT_RETURN_PERCENT,
  MetricKind.PRODUCT_CR,
  MetricKind.PRODUCT_PSV,
  MetricKind.PRODUCT_ITEMS_SOLD,
  MetricKind.PRODUCT_REVENUE,
  MetricKind.PRODUCT_PROFIT,
  MetricKind.PRODUCT_PROFIT_PERCENT,
  MetricKind.PRODUCT_PROFIT_PER_SESSION,
  MetricKind.PRODUCT_TRANSACTIONS,
  MetricKind.PRODUCT_IPP,
];
const getControl = (p: PriceTestProductSummary) =>
  p.variants?.find((x) => x.isControl);
export const getUplift = (
  v: PriceTestProductVariation,
  p: PriceTestProductSummary,
  calc: (v: any) => number,
  controlScale = 1,
  variantScale = 1,
) => {
  const control = getControl(p);
  const denum = maybe(() => calc(control), NaN) * controlScale;
  if (!Number.isFinite(denum) || denum === 0) {
    return 0;
  }
  return ((calc(v) * variantScale) / denum - 1) * 100;
};
